'use client';
import { Filters } from '@/components/filter';
import { QuestionList } from '@/components/question-list';
import { useQuestion } from '@/hooks/useQuestion';
import { useSetupProfile } from '@/hooks/useSetupProfile';
import {
  getParentCategoryNamesForStarterSlug,
  isKnownStarterSlug,
  resolveStarterToSubcategoryIds,
} from '@/lib/idea-starter-tile-mapping';
import { useSearchParams } from 'next/navigation';
import type { CSSProperties } from 'react';
import { useEffect, useLayoutEffect, useState } from 'react';

const IDEA_STARTER_LIMIT = 10;

export default function IdeaStarters() {
  const searchParams = useSearchParams();
  const starterSlug = searchParams.get('starter');

  const { getCategoriesApi } = useSetupProfile({
    loadCategories: true,
    filterType: 'IDEA_STARTER',
  });

  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);

  // Landing from a mid-page homepage scroll can leave the window offset; tiles
  // should open this view at the top. Also avoids fighting scroll anchoring when
  // filters + infinite query hydrate after navigation.
  useLayoutEffect(() => {
    if (!starterSlug) return;
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [starterSlug]);

  useEffect(() => {
    // Main nav opens `/idea-starters` with no `starter` — clear tile session filters
    // so checkboxes and the question query match the full Idea Starters view.
    if (!starterSlug) {
      setSelectedFilters([]);
      return;
    }

    if (!isKnownStarterSlug(starterSlug)) {
      setSelectedFilters([]);
      return;
    }

    const groups = getCategoriesApi.data?.data;
    if (!groups) return;

    setSelectedFilters(resolveStarterToSubcategoryIds(starterSlug, groups));
  }, [starterSlug, getCategoriesApi.data]);

  const visibleParentCategoryNames =
    starterSlug && isKnownStarterSlug(starterSlug)
      ? getParentCategoryNamesForStarterSlug(starterSlug)
      : undefined;

  // Use the infinite query instead of the regular query
  const { getInfiniteQuestionsApi, getAllQuestions } = useQuestion({
    limit: IDEA_STARTER_LIMIT,
    categoryId: selectedFilters,
    type: 'IDEA_STARTER',
  });

  // Get all questions from all pages
  const questions = getAllQuestions();
  const isLoading = getInfiniteQuestionsApi.isLoading;
  const isFetchingNextPage = getInfiniteQuestionsApi.isFetchingNextPage;
  const hasNextPage = getInfiniteQuestionsApi.hasNextPage;

  return (
    <section className="grid grid-cols-[300px_1fr] gap-12 p-8 max-w-5xl w-full mx-auto">
      <Filters
        selectedFilters={selectedFilters}
        setSelectedFilters={setSelectedFilters}
        filterType="IDEA_STARTER"
        visibleParentCategoryNames={visibleParentCategoryNames}
        title="Browse Categories"
      />

      <div
        className="flex flex-col gap-4"
        style={{ overflowAnchor: 'none' } as CSSProperties}
      >
        <div>
          <h1 className="text-base font-bold text-[#24292E]">
            Idea Starter Questions
          </h1>
          <p className="text-sm text-gray-500">
            Explore curated workplace questions by selecting a category on the
            left. Search for specific topics at any time using the search bar
            above.
          </p>
        </div>
        {questions.length === 0 && !isLoading ? (
          <div className="flex items-center justify-center p-20">
            <p className="text-sm text-gray-500">
              No idea starter questions found.
            </p>
          </div>
        ) : (
          <QuestionList
            questions={questions}
            isLoading={isLoading}
            hideUserInfo
            hideReport
            // Add infinite scroll props
            isInfiniteScroll={true}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            fetchNextPage={getInfiniteQuestionsApi.fetchNextPage}
          />
        )}
      </div>
    </section>
  );
}
